/**
 * React Query hooks over the access-control (RBAC) API. Query hooks unwrap the
 * 200 body; mutation hooks throw on non-success for the global error handler.
 */

import { useAuthStore } from '@/domains/auth';
import { accessControlQueryClient } from '@/lib/api/ts-rest-client';
import { queryKeys } from '@/lib/query-client';
import type { CreateRoleBody, UpdateRoleBody } from '@skewb-works/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '../api/errorBody';

const accessControlQueryOptions = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  retry: 1,
  retryDelay: 2000,
};

/**
 * Fetch the current user's effective permissions as packed CASL rules.
 *
 * Gated on authentication: the endpoint sits behind `userAuth`, so firing it before login
 * returns a 401 that would cache as deny-all (staleTime) and — because this query backs the
 * app-wide ability and never refetches on login — leave the freshly logged-in user with an
 * empty sidebar until a manual refresh (WRK-5474 regression). Enabling only once
 * `isAuthenticated` flips true makes react-query fetch real rules the moment login completes.
 */
export function useAccessControlPermissions() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queryKey = queryKeys.accessControl.permissions();

  const query = accessControlQueryClient.getPermissions.useQuery(
    queryKey,
    {},
    { ...accessControlQueryOptions, queryKey, enabled: isAuthenticated },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/** Fetch all roles with permission counts. */
export function useRoles(enabled = true) {
  const queryKey = queryKeys.accessControl.roles();

  const query = accessControlQueryClient.listRoles.useQuery(
    queryKey,
    {},
    { ...accessControlQueryOptions, queryKey, enabled },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/** Fetch a single role and its (action, subject) permissions. */
export function useRole(id: number | undefined) {
  const queryKey = queryKeys.accessControl.role(id ?? 0);

  const query = accessControlQueryClient.getRole.useQuery(
    queryKey,
    { params: { id: id ?? 0 } },
    { ...accessControlQueryOptions, queryKey, enabled: id !== undefined },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/** Fetch the flat list of access-control subjects (grouping is a frontend concern). */
export function useSubjects(enabled = true) {
  const queryKey = queryKeys.accessControl.subjects();

  const query = accessControlQueryClient.listSubjects.useQuery(
    queryKey,
    {},
    { ...accessControlQueryOptions, queryKey, enabled },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/** Fetch the ids of the roles a user currently holds (F0 endpoint). */
export function useUserRoles(userId: number | undefined) {
  const queryKey = queryKeys.accessControl.userRoles(userId ?? 0);

  const query = accessControlQueryClient.getUserRoles.useQuery(
    queryKey,
    { params: { userId: userId ?? 0 } },
    { ...accessControlQueryOptions, queryKey, enabled: userId !== undefined },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/** Create a new role. */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRoleBody) => {
      const result = await accessControlQueryClient.createRole.mutation({ body: data });
      if (result.status !== 201) {
        throw new Error(getErrorMessage(result.body, 'Failed to create role'));
      }
      return result.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.roles() });
    },
    meta: { resourceName: 'Role', action: 'create' },
  });
}

/** Update a role and/or replace its permission set. */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateRoleBody }) => {
      const result = await accessControlQueryClient.updateRole.mutation({
        params: { id },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error(getErrorMessage(result.body, 'Failed to update role'));
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.roles() });
      // Drop the cached detail rather than just invalidating it: the editor seeds its form state
      // from the role once at mount, so a stale-while-revalidate hit would show old permissions
      // until refresh. Removing it forces the next visit to refetch behind the loader.
      queryClient.removeQueries({ queryKey: queryKeys.accessControl.role(variables.id) });
    },
    meta: { resourceName: 'Role', action: 'update' },
  });
}

/** Delete a non-system role. */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await accessControlQueryClient.deleteRole.mutation({
        params: { id },
        body: undefined,
      });
      if (result.status !== 200) {
        throw new Error(getErrorMessage(result.body, 'Failed to delete role'));
      }
      return result.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.roles() });
    },
    meta: { resourceName: 'Role', action: 'delete' },
  });
}

/** Assign a role to a user (writes access_control_user_role). */
export function useAssignUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: number; roleId: number }) => {
      const result = await accessControlQueryClient.assignUserRole.mutation({
        params: { userId },
        body: { roleId },
      });
      if (result.status !== 200) {
        throw new Error(getErrorMessage(result.body, 'Failed to assign role'));
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accessControl.userRoles(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(String(variables.userId)),
      });
    },
    meta: {
      successTitle: 'Role Assigned',
      successMessage: 'The role has been assigned successfully',
    },
  });
}

/** Remove a role from a user. */
export function useRemoveUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: number; roleId: number }) => {
      const result = await accessControlQueryClient.removeUserRole.mutation({
        params: { userId, roleId },
        body: undefined,
      });
      if (result.status !== 200) {
        throw new Error(getErrorMessage(result.body, 'Failed to remove role'));
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accessControl.userRoles(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(String(variables.userId)),
      });
    },
    meta: {
      successTitle: 'Role Removed',
      successMessage: 'The role has been removed successfully',
    },
  });
}
